import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { DropBase } from '@hh.ru/magritte-internal-drop-base';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { s as styles, D as DropContainer } from './DropContainer-Dxs1sGPq.js';
import { PureChildren } from './PureChildren.js';
import { Layer } from '@hh.ru/magritte-ui-layer';
import '@hh.ru/magritte-common-use-multiple-refs';
import '@hh.ru/magritte-ui-divider';

const PLACEMENTS_LIST = [
    'left-top',
    'left-bottom',
    'right-top',
    'right-bottom',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
];
const isKeyboardInput = (element) => {
    const tagName = element.tagName.toLowerCase();
    const keyboardInputTypes = ['text', 'password', 'email', 'number', 'tel', 'url', 'search'];
    return ((tagName === 'input' && keyboardInputTypes.includes(element.type)) ||
        tagName === 'textarea' ||
        element.hasAttribute('contenteditable'));
};
const DropComponent = ({ visible, activatorRef, onClose, maxWidth = 320, minHeight = 200, width, space = 600, children, footer, role = 'dialog', dividerState = 'show', widthEqualToActivator = false, allowShrinkHeightToFitIntoViewport = true, autoFocusWhenOpened, closeByClickOutside = true, 'data-qa': dataQA = 'drop', onAppear, onBeforeExit, onAfterExit, placement, forcePlacement, ignoreIntersections, lockPlacement = false, ...props }, ref) => {
    const { isMobile } = useBreakpoint();
    const [animationTimeout, setAnimationTimeout] = useState(200);
    const [isPlacementLockedByFocus, setPlacementLockedByFocus] = useState(false);
    const forceUpdateDropMetricsRef = useRef();
    const actualIgnoreIntersections = ignoreIntersections === undefined ? !allowShrinkHeightToFitIntoViewport : ignoreIntersections;
    const placementsList = useMemo(() => {
        const placementsArray = Array.isArray(placement) ? placement : [placement];
        return (forcePlacement
            ? placementsArray
            : [...placementsArray, ...PLACEMENTS_LIST.filter((place) => !placement.includes(place))]);
    }, [placement, forcePlacement]);
    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.dropAnimationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const animationTimeout = parseInt(style.getPropertyValue(`--animation-duration`), 10);
        if (Number.isInteger(animationTimeout)) {
            setAnimationTimeout(animationTimeout);
        }
        document.body.removeChild(animationTimeoutElement);
    }, []);
    useEffect(() => {
        forceUpdateDropMetricsRef.current?.();
    }, [maxWidth, space, footer]);
    const focusHandler = (event) => {
        const target = event.target;
        if (!target || !isKeyboardInput(target)) {
            return;
        }
        setPlacementLockedByFocus(true);
    };
    const blurHandler = () => setPlacementLockedByFocus(false);
    useEffect(() => {
        if (!visible || isMobile || !activatorRef.current) {
            return void 0;
        }
        const activatorElement = activatorRef.current;
        activatorElement.addEventListener('focus', focusHandler);
        activatorElement.addEventListener('blur', blurHandler);
        return () => {
            activatorElement.removeEventListener('focus', focusHandler);
            activatorElement.removeEventListener('blur', blurHandler);
        };
    }, [visible, activatorRef, isMobile]);
    if (isMobile) {
        return null;
    }
    let widthStyle;
    if (widthEqualToActivator) {
        widthStyle = { width: '100%' };
    }
    else {
        widthStyle = width ? { width } : { maxWidth };
    }
    return (jsx(Layer, { layer: "drop", children: jsx(DropBase, { ...props, visible: visible, placement: placementsList, activatorRef: activatorRef, closeByClickOutside: closeByClickOutside, onClose: onClose, autoFocusWhenOpened: autoFocusWhenOpened ?? role !== 'status', animationClassNames: {
                enter: styles.dropEnter,
                enterActive: styles.dropEnterActive,
                exit: styles.dropExit,
                exitActive: styles.dropExitActive,
            }, animationTimeout: animationTimeout, widthEqualToActivator: widthEqualToActivator, allowShrinkHeightToFitIntoViewport: allowShrinkHeightToFitIntoViewport, ignoreIntersections: actualIgnoreIntersections, minHeight: minHeight, ref: ref, onAppear: onAppear, onBeforeExit: onBeforeExit, onAfterExit: onAfterExit, forceDropMetricsUpdate: forceUpdateDropMetricsRef, lockPlacement: isPlacementLockedByFocus || lockPlacement, children: (resizeRefCallbackFactory) => (jsxs("div", { className: classnames(styles.drop, {
                    [styles.dropSpace300]: space === 300,
                    [styles.dropSpace400]: space === 400,
                    [styles.dropSpace600]: space === 600,
                    [styles.dropWithFooter]: !!footer,
                }), style: widthStyle, "data-qa": dataQA, onFocus: focusHandler, onBlur: blurHandler, children: [jsx(DropContainer, { dividerState: !footer ? 'hide' : dividerState, ref: resizeRefCallbackFactory('scroll-container', false, true), children: jsx("div", { ref: resizeRefCallbackFactory('drop-content'), children: jsx(PureChildren, { children: children }) }) }, "drop-container"), footer && (jsx("div", { ref: resizeRefCallbackFactory('drop-footer'), className: styles.dropFooter, children: jsx(PureChildren, { children: footer }) }))] }, "drop-outer-container")) }) }));
};
const Drop = forwardRef(DropComponent);

export { Drop };
//# sourceMappingURL=Drop.js.map
